// Convert a reminder time value to 12-hour display format (e.g., "9:00 AM").
//
// Accepts two shapes for back-compat:
//   • Numeric seconds since midnight (legacy) → converted.
//   • Already-formatted string like "09:00 AM" / "9:00 PM" (current backend
//     shape) → normalised and passed through. Previously this hit
//     `parseInt("09:00 AM", 10) === 9`, then `9 / 3600 === 0` → every
//     reminder rendered as "12:00 AM".
export const secondsToTimeFormat = (seconds: number | string): string => {
  if (typeof seconds === "string") {
    const formattedMatch = seconds
      .trim()
      .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (formattedMatch) {
      const hour = parseInt(formattedMatch[1], 10);
      const minute = formattedMatch[2];
      const ampm = formattedMatch[3].toUpperCase();
      return `${hour}:${minute} ${ampm}`;
    }
  }

  const totalSeconds =
    typeof seconds === "string" ? parseInt(seconds, 10) : seconds;
  if (!Number.isFinite(totalSeconds)) return String(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};

// Define reminder options
export const reminderOptions = (t: any, key: string = "vaccinations") => [
  { value: 0, label: t(`reminder.options.${key}.day`) },
  { value: 1, label: t("reminder.options.1") },
  { value: 2, label: t("reminder.options.2") },
  { value: 3, label: t("reminder.options.3") },
];

export const getReminderOptions = (
  dueDate: Date,
  t: any,
  key: string = "vaccinations"
) => {
  if (!dueDate)
    return [{ value: 0 + "", label: t(`reminder.options.${key}.day`) }];

  // Calculate days between today and due date
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time part for accurate day calculation
  const dueDateCopy = new Date(dueDate);
  dueDateCopy.setHours(0, 0, 0, 0);

  // Calculate difference in days
  const diffTime = dueDateCopy.getTime() - today.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Create options based on days difference (up to 3 days before)
  const options = [{ value: 0 + "", label: t(`reminder.options.${key}.day`) }];

  // Add options for 1, 2, 3 days before if applicable
  for (let i = 1; i <= Math.min(3, diffDays); i++) {
    options.push({ value: i + "", label: t(`reminder.options.${i}`) });
  }

  return options;
};
